import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import { ChangeEvent, FC, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { useReopenedStages } from "../../../hooks/useReopenedStages";
import { TJobPreferencesFormValues } from "../../../models/Onboarding/onboarding";
import { MODE_OF_TRANSPORT } from "../../../models/Onboarding/onboarding-values";
import Roles from "../../../models/roles";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import { handleSubmitStage } from "../../../services/onlineInterviewService";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { getNearbySuburbsThunk } from "../../../store/auth/thunks";
import { TNearbySuburb } from "../../../store/auth/types";
import { jobPreferencesThunk } from "../../../store/onboarding/thunk";
import { TSubUrbs } from "../../../store/onboarding/types";
import { getCandidateData } from "../../../store/superadmin/thunks";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import { computeMissingDocs } from "../../../utils/missingDocsHelper";
import CustomButton from "../../UI/Buttons/CustomButton";
import CircleTick from "../../UI/Icons/CircleTick";
import LoadingArrow from "../../UI/Icons/LoadingArrow";
import RightArrow from "../../UI/Icons/RightArrow";
import RoundedTick from "../../UI/Icons/RoundedTick";
import CheckboxWithIcon from "../../UI/Inputs/ChecboxWithIcon";
import MandatoryDocsAlert from "../../UI/Inputs/MandatoryDocsAlert";
import RangeSelector from "../../UI/Inputs/RangeSelector";
import TagsInput from "../../UI/Inputs/TagsInput";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";
import "./JobPreferences.scss";

const JobPreferences: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isFirstLoad = useRef(true);
  const { isImpersonating, impersonationData } = useImpersonation();

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [pendingSteps, setPendingSteps] = useState<string>('');
  const [showSubmitBtnTooltip, setShowSubmitBtnTooltip] = useState<boolean>(false);
  const [isLoadingSuburbs, setIsLoadingSuburbs] = useState<boolean>(true);
  const [removedSuburbs, setRemovedSuburbs] = useState<string[]>([]);
  const [searchSuburb, setSearchSuburb] = useState("");
  const [showMaxDistanceAlert, setShowMaxDistanceAlert] = useState<boolean>(false);

  const inProgress = useSelector((state) => state.auth.inProgress);
  const user = useSelector((state) => state.auth.user);
  const nearBySuburbs = useSelector((state) => state.auth.nearbySuburbs);
  // Check if the logged-in user is a Super Admin (used for editing candidate profiles)
  const isSuperAdmin =
    (user?.role?.name as Roles) === Roles.SUPER_ADMIN ||
    (user?.role?.name as Roles) === Roles.GENERAL_ADMIN;

  /** Get the values from the state */
  const jobPreferences = useSelector((state) =>
    !isSuperAdmin
      ? state.onboarding.jobpreferences
      : state.superadmin.reviewCandidateData?.jobpreference?.[0]
  );
  const userpersonaldetails = useSelector((state) =>
    !isSuperAdmin
      ? state.onboarding.userpersonaldetails
      : state.superadmin.reviewCandidateData
  );
  const personaldetails = useSelector((state) => state.onboarding.personaldetails);
  const qualifications = useSelector((state) => state.onboarding.qualifications);

  const [maxDistance, setMaxDistance] = useState<number>(0);
  const [suburbs, setSuburbs] = useState<TNearbySuburb[]>([]);
  const [newSuburbs, setNewSuburbs] = useState(nearBySuburbs);
  const [selectedSuburbs, setSelectedSuburbs] = useState<string[]>([]);
  const [isAddingSuburbs, setIsAddingSuburbs] = useState<boolean>(false);
  const [loadedDistance, setLoadedDistance] = useState<number>(jobPreferences?.max_travel_distance || 0);
  const modeOfTransports = [
    MODE_OF_TRANSPORT.CAR,
    MODE_OF_TRANSPORT.BUS,
    MODE_OF_TRANSPORT.TRAIN,
    MODE_OF_TRANSPORT.TRAM,
  ];
  const [selectedModeOfTransport, setSelectedModeOfTransport] = useState<
    string[]
  >([]);
  const [checkPath, setCheckPath] = useState<boolean>(false);

  const { isSectionReopenedFn, reopenedSections } = useReopenedStages();
  const isJobPreferencesReopened = isSectionReopenedFn('jobpreference');
  const isOnlineInterviewReopened = isSectionReopenedFn('online_interview');

  const handleMouseEnter = () => setShowSubmitBtnTooltip(true);
  const handleMouseLeave = () => setShowSubmitBtnTooltip(false);

  useEffect(() => {
    const missingPersonal = personaldetails?.resume === null || personaldetails === null;
    const missingQualification = computeMissingDocs(
      qualifications,
      personaldetails?.working_rights,
      qualifications?.documents
    );
    const steps: string[] = [];
    if (missingPersonal) steps.push("Personal Details");
    if (missingQualification.length > 0) steps.push("Qualifications");
    setPendingSteps(steps.join(", "));
  }, [qualifications, personaldetails]);
  const fetchNearbySuburbs = async (
    maxDistance: number,
    searchText?: string
  ) => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getNearbySuburbsThunk({
        address: userpersonaldetails?.address as string,
        radius: maxDistance,
        ...(searchText ? { search: searchText } : {}),
      })
    );
    dispatch(hideTransparentPreloaderAction());
  };

  useEffect(() => {
    if (jobPreferences && jobPreferences !== null) {
      setMaxDistance(jobPreferences.max_travel_distance);
      setLoadedDistance(jobPreferences.max_travel_distance);
      setSelectedModeOfTransport(
        jobPreferences.preferred_transport_modes !== null
          ? JSON.parse(jobPreferences.preferred_transport_modes)
          : []
      );
    }
  }, [jobPreferences, setMaxDistance, setSelectedModeOfTransport]);

  /** Update the values in the array for checkbox of modes of transport */
  const handleModeOfTransport = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    const value = e.target.value;
    setSelectedModeOfTransport(
      checked
        ? [...selectedModeOfTransport, value]
        : selectedModeOfTransport.filter((item) => item !== value)
    );
  };

  /** Submit the form for jobPreferences */
  const { handleSubmit, values, errors, setFieldValue } = useFormik({
    validationSchema: createValidationSchema("jobPreferences"),
    validateOnBlur: false,
    validateOnChange: false,
    initialValues: {
      max_travel_distance: jobPreferences?.max_travel_distance || 0,
      suburbs: selectedSuburbs || [],
      preferred_transport_modes: selectedModeOfTransport || [],
    },
    onSubmit: async (values: TJobPreferencesFormValues) => {
      const payload = { ...values };
      // Only set when a Super‑Admin edits a candidate profile; required by the API to identify the candidate.
      if (
        isSuperAdmin &&
        userpersonaldetails &&
        "uuid" in userpersonaldetails
      ) {
        payload.candidate_uuid = userpersonaldetails.uuid;
      } else if (isImpersonating && impersonationData?.candidate?.uuid) {
        payload.candidate_uuid = impersonationData.candidate.uuid;
      }
      payload.isImpersonating = isImpersonating;
      dispatch(showTransparentPreloaderAction());
      try {
        const result = await dispatch(jobPreferencesThunk(payload));
        if (isFulfilled(result)) {
          if (checkPath) {
            if (isSuperAdmin) {
              const candidateResponse = await dispatch(
                getCandidateData({ candidate_id: userpersonaldetails?.uuid })
              );
              if (isFulfilled(candidateResponse)) {
                dispatch(showNotificationAction("Updated"));
              }
            } else {
              navigate("/candidate/my-profile/online-interview");
            }
          } else {
            navigate(AUTH_ROUTE.ONLINE_INTERVIEW);
          }
        }
      } catch (error) {
        throw new Error(
          "Could not connect to services at the moment. Please try again. Thanks."
        );
      }
      dispatch(hideTransparentPreloaderAction());
    },
  });

  /** Check and update the values */
  useEffect(() => {
    // if (maxDistance !== 0) {
      setFieldValue("max_travel_distance", maxDistance);
    // }

    // if (selectedSuburbs?.length > 0) {
      setFieldValue("suburbs", selectedSuburbs);
    // }

    if (selectedModeOfTransport) {
      setFieldValue("preferred_transport_modes", selectedModeOfTransport);
    }
  }, [setFieldValue, maxDistance, selectedSuburbs, selectedModeOfTransport]);

  useEffect(() => {
    if (location.pathname.includes("candidate")) {
      setCheckPath(true);
    }
  }, [location]);

  useEffect(() => {
    setNewSuburbs(nearBySuburbs);

    // 1. Transform saved jobPreferences suburbs
    const input = jobPreferences?.jobpreferencesubrubs ?? [];

    const transformed: TNearbySuburb[] = input.map((suburb: TSubUrbs, index: number) => {
      const isManual = suburb.entre_type === "M" || suburb.suburb.endsWith("- M");

      // Remove any trailing "- digits" or "- M"
      const cleanName = suburb.suburb.replace(/\s*-\s*\d+$/, "").replace(/\s*-\s*M$/, "");

      // Build full label with postcode
      const label = `${cleanName}`;

      return {
        id: 100000 + index,
        suburb: cleanName,
        postcode: suburb.pincode,
        lat: 0,
        lng: 0,
        address: "",
        label: label,
        search_type: isManual ? "manual" : (suburb.entre_type ?? ""),
      };
    });

    const newSuburbKeys = new Set(
      (newSuburbs ?? []).map((suburb) => `${suburb.label} - ${suburb.postcode}`)
    );

        // On initial load or when we have saved suburbs, always include them
    // Only filter when we have API suburbs loaded (newSuburbs has items)
    const filteredTransformed = transformed.filter((suburb) => {
      // Always keep manual suburbs
      if (suburb.label.endsWith(" - M") || suburb.search_type === "manual") {
        return true;
      }
      // If API suburbs haven't loaded yet, keep all saved suburbs
      if (newSuburbKeys.size === 0) {
        return true;
      }
      // If API suburbs loaded, only keep saved suburbs that exist in API response
      return newSuburbKeys.has(`${suburb.label}`);
    });

    // 2. Merge API suburbs + transformed
    let allSuburbs: TNearbySuburb[] = [...(newSuburbs ?? []), ...filteredTransformed];

    // 3. Deduplicate by label+postcode
    const uniqueMap = new Map<string, TNearbySuburb>();
    allSuburbs.forEach((s) => {
      const key = `${s.label} - ${s.postcode}`;
      if (!uniqueMap.has(key)) uniqueMap.set(key, s);
    });
    let deduplicatedSuburbs = Array.from(uniqueMap.values());

    deduplicatedSuburbs = Array.from(uniqueMap.values());

    // 4. Update suburbs list (for dropdown)
    // setSuburbs(deduplicatedSuburbs);
    
    // 4. Update suburbs list (for dropdown)
    const isManualSearch = Boolean(searchSuburb);

    const selectedSuburbKeys = new Set(
      selectedSuburbs.map((s) => s.replace(" - M", ""))
    );

    const removedSuburbKeys = new Set(
      removedSuburbs.map((s) => s.replace(" - M", ""))
    );

    // ✅ During manual search, show all suburbs including selected ones (with checked checkboxes)
    // Only exclude suburbs that user explicitly removed
    const filteredDropdownSuburbs = isManualSearch
      ? deduplicatedSuburbs.filter((suburb) => {
          const key = `${suburb.label} - ${suburb.postcode}`;
          return !removedSuburbKeys.has(key);
        })
      : deduplicatedSuburbs;

    setSuburbs(filteredDropdownSuburbs);

    // 5. Update selectedSuburbs
    if (searchSuburb) {
      // If this was a manual search, don’t auto‑select anything
      return;
    }

    // 6. If maxDistance changed or initial load:
    setSelectedSuburbs((prev) => {
      // Keep manual (- M) selections
      const manualSelections = prev.filter((s) => s.endsWith('- M'));

      const savedSelections = transformed.map(
        (s) =>
          `${s.label}${s.postcode ? ` - ${s.postcode}` : ""}${
            s.search_type === "manual" ? " - M" : ""
          }`
      );

      const savedKeys = new Set(
        transformed.map(
          (s) => `${s.label}${s.postcode ? ` - ${s.postcode}` : ""}`
        )
      );

      // Auto‑select all API suburbs
      if (isFirstLoad.current) {
        // ✅ Initial load: only keep manual + saved ones
        isFirstLoad.current = false;

        const filteredSavedSelections = savedSelections.filter(
          (s) => !removedSuburbs.includes(s.replace(" - M", ""))
        );

        return Array.from(new Set([...filteredSavedSelections]));
      } else {
        // Get all non-manual suburbs from API response
        let autoSelections: any = deduplicatedSuburbs.filter((s) => s.search_type !== "manual");

        // When distance changed (reload happened), show ALL new suburbs from API
        // Don't filter by savedKeys unless it's the same distance as original
        const isOriginalDistance = maxDistance === jobPreferences?.max_travel_distance;
        const hasReloaded = maxDistance === loadedDistance;
        
        if (savedKeys.size > 0 && isOriginalDistance && hasReloaded && !isAddingSuburbs) {
          // Only filter by saved suburbs if we're at original distance and haven't manually added suburbs
          autoSelections = autoSelections.filter((s: any) =>
            savedKeys.has(`${s.label} - ${s.postcode}`)
          );
        }

        // Format auto-selections and filter out duplicates with manual selections
        autoSelections = autoSelections
          .map((s: any) => `${s.label} - ${s.postcode}`)
          .filter(
            (auto: any) =>
              !manualSelections.some((manual) =>
                auto.startsWith(manual.replace(" - M", ""))
              )
          )
          .filter((auto: any) => !removedSuburbs.includes(auto));
        
        // Always preserve manual selections + auto selections based on current distance
        if(!isAddingSuburbs && isOriginalDistance) {
          return Array.from(
            new Set([...manualSelections, ...savedSelections, ...autoSelections])
          );
        } else {
          // When distance changed or adding suburbs manually, only keep manual + new API suburbs
          return Array.from(
            new Set([...manualSelections, ...autoSelections])
          );
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearBySuburbs, jobPreferences, newSuburbs, searchSuburb]);

  useEffect(() => {
    if(selectedSuburbs.length > 0) {
      const selectedSuburbObjects: TNearbySuburb[] = selectedSuburbs.map((s, index) => {
        const isManual = s.endsWith(" - M");
        const cleaned = s.replace(" - M", "");
        const [label, postcode] = cleaned.split(" - ");

        return {
          id: 200000 + index, // use a different range to avoid collision
          suburb: label,
          postcode: postcode ?? "",
          lat: 0,
          lng: 0,
          address: "",
          label: label,
          search_type: isManual ? "manual" : "auto",
        };
      });

      const selectedSuburbsList = [
        ...(newSuburbs ?? []),
        ...selectedSuburbObjects,
      ];

      const uniqueMap = new Map<string, TNearbySuburb>();
      selectedSuburbsList.forEach((s) => {
        const key = `${s.label} - ${s.postcode}`;
        if (!uniqueMap.has(key)) uniqueMap.set(key, s);
      });

      const deduplicatedSuburbs = Array.from(uniqueMap.values());

      const isManualSearch = Boolean(searchSuburb);

      const selectedSuburbKeys = new Set(
        selectedSuburbs.map((s) => s.replace(" - M", ""))
      );

      const removedSuburbKeys = new Set(
        removedSuburbs.map((s) => s.replace(" - M", ""))
      );

      // ✅ During manual search, show all suburbs including selected ones (with checked checkboxes)
      // Only exclude suburbs that user explicitly removed
      const filteredDropdownSuburbs = isManualSearch
        ? deduplicatedSuburbs.filter((suburb) => {
            const key = `${suburb.label} - ${suburb.postcode}`;
            return !removedSuburbKeys.has(key);
          })
        : deduplicatedSuburbs;

      setSuburbs(filteredDropdownSuburbs);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSuburbs, newSuburbs, searchSuburb]);

  useEffect(() => {
    // If distance is zero, don't auto-populate suburbs
    if (maxDistance === 0 && selectedSuburbs.length > 0) {
      setSuburbs([]);
      setSelectedSuburbs([]);
      setHasLoadedOnce(false);
      // Also reset loadedDistance so the user can reload suburbs
      // after they change the distance back from 0.
      setLoadedDistance(0);
      return;
    }
  }, [maxDistance, selectedSuburbs]);

  const handleSuburbsChange = (tags: string | Array<string>) => {
    if (!Array.isArray(tags)) return;

    const newSelected = tags?.map((label) => label);
    const removed = selectedSuburbs.filter((s) => !newSelected.includes(s));
    const reAdded = removedSuburbs.filter((s) => {
      // Check if this removed suburb is being re-added (with or without " - M")
      return newSelected.some(sel => 
        sel.replace(" - M", "") === s.replace(" - M", "")
      );
    });

    setSelectedSuburbs(newSelected);

    // Only update removedSuburbs if NOT in search mode
    // During search, unchecking a checkbox should NOT hide the item from dropdown
    // Only clicking X button on tags (when not searching) should hide items
    if (!searchSuburb) {
      setRemovedSuburbs((prev) => {
        const updated = [...prev, ...removed];
        const final = updated.filter((suburb) => {
          // Remove from removedSuburbs if it was re-added
          return !reAdded.some(readd => 
            readd.replace(" - M", "") === suburb.replace(" - M", "")
          );
        });
        return Array.from(new Set(final));
      });
    } else {
      // During search, only clear re-added items from removedSuburbs
      if (reAdded.length > 0) {
        setRemovedSuburbs((prev) => {
          return prev.filter((suburb) => {
            return !reAdded.some(readd => 
              readd.replace(" - M", "") === suburb.replace(" - M", "")
            );
          });
        });
      }
    }
  };

  const handleReloadSuburbs = async () => {
    setIsLoadingSuburbs(true);
    setHasLoadedOnce(true);
    setSearchSuburb("");
    await fetchNearbySuburbs(maxDistance).then(() => {
      setHasLoadedOnce(true);
      setIsAddingSuburbs(false);
      setLoadedDistance(maxDistance);
    });
  };

  const handleSearch = async (searchText: string) => {
    if (!userpersonaldetails?.address || maxDistance < 1) return;
    setIsLoadingSuburbs(true);
    setHasLoadedOnce(true);
    setSearchSuburb(searchText);
    await fetchNearbySuburbs(maxDistance, searchText).then(() => {
      setHasLoadedOnce(true);
    });
  };

  /** Check all the values got updated for enabling the next button */
  useEffect(() => {
    if (
      values.max_travel_distance !== 0 &&
      values.preferred_transport_modes.length > 0 &&
      values.suburbs.length > 0
    ) {
      setIsDisabled(!true);
    } else {
      setIsDisabled(true);
    }
  }, [values]);

  return (
    
      <div className="d-flex flex-column gap-20 max-w-650px">
        <div className="d-flex gap-20 align-items-center justify-content-between">
          <div className="fs-30 fw-400 text-black">Job Preferences</div>
          {isSuperAdmin && (
            <CustomButton
              variant={"primary"}
              type="button"
              className="btn-theme bg-teal text-black"
              onClick={handleSubmit}
            >
              <RoundedTick />
              Save Changes
            </CustomButton>
          )}
        </div>
        <div className="d-flex flex-column fs-16 text-black">
          <div className="fw-400">You've noted your home address as:</div>
          <div className="fw-700">{userpersonaldetails?.address}</div>
        </div>
        <div className="d-flex flex-column gap-20">
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-black">
              Set your maximum travel distance from home:
            </div>
            <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center">
              <RangeSelector
                min={0}
                max={100}
                setMaxDistance={setMaxDistance}
                setIsLoadingSuburbs={setIsLoadingSuburbs}
                setShowMaxDistanceAlert={setShowMaxDistanceAlert}
                rangeType="haveIcon"
              />
              <CustomButton
                variant={"primary"}
                isLoading={inProgress}
                onClick={handleReloadSuburbs}
                disabled={isLoadingSuburbs || maxDistance === loadedDistance}
                className="btn-theme bg-teal text-black min-w-126px mobile-w-100"
              >
                {!inProgress ? <LoadingArrow /> : ""}
                {hasLoadedOnce ? "Reload Suburbs" : "Load Suburbs"}
              </CustomButton>
            </div>
            {showMaxDistanceAlert && <ErrorMessage className="align-items-center gap-3 lh-1px" message="Maximum distance cannot exceed 100km." />}
          </div>
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-black user-select-none">
              Remove or add suburbs to your preferred job locations:
            </div>
            <TagsInput
              onSelectionChange={handleSuburbsChange}
              selectedItems={selectedSuburbs}
              items={suburbs}
              onSearch={handleSearch}
              isSuburb={true}
            />
            {errors && <ErrorMessage message={errors.suburbs as string} />}
          </div>
          <div className="d-flex flex-column gap-10">
            <div className="fs-16 fw-400 text-black">
              Select you preferred mode/s of transport:
            </div>
            <div className="d-flex gap-10 modes-of-trensport-container">
              {modeOfTransports &&
                modeOfTransports?.map((modeOfTransport: string) => {
                  const transportsLc = modeOfTransport.toLowerCase();
                  return (
                    <div key={uuidv4()} className="mobile-w-50">
                      <CheckboxWithIcon
                        id={`transport_${transportsLc}`}
                        value={modeOfTransport}
                        checked={selectedModeOfTransport.includes(
                          modeOfTransport
                        )}
                        label={modeOfTransport}
                        onChange={handleModeOfTransport}
                        iconClass={`icon-${transportsLc}`}
                      />
                    </div>
                  );
                })}
            </div>
            {errors && (
              <ErrorMessage
                message={errors.preferred_transport_modes as string}
              />
            )}
          </div>
          <div className="d-flex flex-column flex-lg-row gap-20 justify-content-between mt-10">
            {!isSuperAdmin && (
              <CustomButton
                type="submit"
                variant="primary"
                disabled={isDisabled}
                onClick={handleSubmit}
                className="btn-theme bg-teal text-black"
              >
                {isJobPreferencesReopened ? "Save" : "Next"}
                <RightArrow color={"#000000"} />
              </CustomButton>
            )}
            {
              isJobPreferencesReopened && !isOnlineInterviewReopened && (
                <div
                  className="position-relative"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <CustomButton
                    type="submit"
                    contentClass="d-flex"
                    variant="primary"
                    disabled={pendingSteps !== ""}
                    onClick={() => handleSubmitStage(dispatch, 1)}
                    className={`btn-theme ${pendingSteps === "" ? 'bg-off-white text-black' : 'bg-grey-light text-grey-medium'}`}
                  >
                    <CircleTick fill={pendingSteps === "" ? "#000000" : "#aaaaaa"} />
                    Resubmit Stage 1
                  </CustomButton>
                  {(showSubmitBtnTooltip && pendingSteps !== "") && <MandatoryDocsAlert pendingSteps={pendingSteps} />}
                </div>
              )
            }
          </div>
        </div>
      </div>
    
  );
};

export default JobPreferences;
